import {
  receiverChallenge,
  receiverSecret,
  startHostedCenterLogin,
} from "@browsemate/auth-client";

const prefix = "browsemate.hosted.";
const epochKey = "browsemate.login.epoch";
const proof = /^[A-Za-z0-9_-]{43}$/;
export interface PendingHostedLogin {
  target: "web" | "extension";
  challenge: string;
  verifier?: string;
  expiresAt: string;
  epoch: string;
}
export function loginEpoch(): string {
  return localStorage.getItem(epochKey) ?? "initial";
}
export function invalidateHostedLogins(): void {
  localStorage.setItem(epochKey, crypto.randomUUID());
  for (let index = sessionStorage.length - 1; index >= 0; index--) {
    const key = sessionStorage.key(index);
    if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
  }
}
export async function beginHostedLogin(
  target: "web" | "extension",
  extensionChallenge: string | undefined,
  current: () => boolean,
  signal: AbortSignal,
): Promise<string> {
  const epoch = loginEpoch();
  const verifier = target === "web" ? receiverSecret() : undefined;
  const challenge = verifier
    ? await receiverChallenge(verifier)
    : extensionChallenge;
  if (!challenge || !proof.test(challenge)) throw new Error("MISSING_RECEIVER");
  if (!current() || loginEpoch() !== epoch) throw new Error("STALE_LOGIN");
  const transaction = await startHostedCenterLogin(
    new URL(location.origin),
    target,
    challenge,
    signal,
  );
  if (!current() || loginEpoch() !== epoch) throw new Error("STALE_LOGIN");
  const pending: PendingHostedLogin = {
    target,
    challenge,
    ...(verifier ? { verifier } : {}),
    expiresAt: transaction.expires_at,
    epoch,
  };
  sessionStorage.setItem(
    prefix + transaction.transaction,
    JSON.stringify(pending),
  );
  return transaction.authorization_url;
}
export function readHostedLogin(
  transaction: string,
  target: string | null,
): PendingHostedLogin {
  if (!proof.test(transaction)) throw new Error("MISSING_TRANSACTION");
  const value = JSON.parse(
    sessionStorage.getItem(prefix + transaction) ?? "null",
  ) as Partial<PendingHostedLogin> | null;
  const expectedTarget = target ?? value?.target;
  if (
    !value ||
    value.target !== expectedTarget ||
    !value.challenge ||
    !proof.test(value.challenge) ||
    typeof value.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(value.expiresAt)) ||
    Date.parse(value.expiresAt) <= Date.now() ||
    value.epoch !== loginEpoch() ||
    (expectedTarget === "web" &&
      (!value.verifier || !proof.test(value.verifier))) ||
    (expectedTarget !== "web" && expectedTarget !== "extension")
  )
    throw new Error("STALE_LOGIN");
  return value as PendingHostedLogin;
}
export function forgetHostedLogin(transaction: string): void {
  if (proof.test(transaction)) sessionStorage.removeItem(prefix + transaction);
}
